import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import * as ContactActions from './contact.actions';
import { ContactService } from '../../services/contact.service';

@Injectable()
export class ContactEffects {
  private actions$ = inject(Actions);
  private contactService = inject(ContactService);

  private getErrorMessage(error: unknown): string {
    console.error('Contact API Error:', error);
    if (error instanceof HttpErrorResponse) {
      console.error('Error details:', error.error);
      if (error.status === 0) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      if (error.status === 400) {
        if (typeof error.error === 'string') {
          return error.error;
        }
        if (error.error?.message) {
          return error.error.message;
        }
        if (error.error?.errors) {
          const errors = Object.values(error.error.errors).flat().join(' ');
          return errors || 'Please check your information and try again.';
        }
        return 'Please check your information and try again.';
      }
      if (error.status === 500) {
        if (error.error?.message) {
          return `Server error: ${error.error.message}`;
        }
        return 'Server error. Please try again later.';
      }
      return error.error?.message || error.message || 'An error occurred. Please try again.';
    }
    return 'An unexpected error occurred. Please try again.';
  }

  submitContactInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactActions.submitContactInquiry),
      mergeMap(({ data }) =>
        this.contactService.submitInquiry(data).pipe(
          map(inquiry => ContactActions.submitContactInquirySuccess({ inquiry })),
          catchError(error =>
            of(ContactActions.submitContactInquiryFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getContactInquiries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactActions.getContactInquiries),
      mergeMap(({ pageNumber = 1, pageSize = 10 }) =>
        this.contactService.getInquiries(pageNumber, pageSize).pipe(
          map(page => ContactActions.getContactInquiriesSuccess({ page })),
          catchError(error =>
            of(ContactActions.getContactInquiriesFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );

  getContactInquiryById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactActions.getContactInquiryById),
      mergeMap(({ id }) =>
        this.contactService.getInquiryById(id).pipe(
          map(inquiry => ContactActions.getContactInquiryByIdSuccess({ inquiry })),
          catchError(error =>
            of(ContactActions.getContactInquiryByIdFailure({ error: this.getErrorMessage(error) }))
          )
        )
      )
    )
  );
}
